import { Calendar, DollarSign, Target, TrendingUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

interface AssetRevenueTabProps {
  templateId: string;
}

const AssetRevenueTab: React.FC<AssetRevenueTabProps> = ({ templateId }) => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    averageDailyRate: 0,
    totalRentalDays: 0,
    roiPercentage: 0,
    revenuePerInstance: 0,
    monthlyRevenue: [] as { month: string; revenue: number }[],
    topPerformingInstances: [] as {
      instance_id: string;
      instance_number: string;
      revenue: number;
      rental_days: number;
    }[],
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRevenueStats();
  }, [templateId]);

  const loadRevenueStats = async () => {
    try {
      setLoading(true);

      // For now, we'll use mock data since the analytics endpoint isn't implemented yet
      // TODO: Replace with actual API call to /api/asset-templates/{templateId}/revenue

      setTimeout(() => {
        setStats({
          totalRevenue: 48750,
          averageDailyRate: 35,
          totalRentalDays: 1392,
          roiPercentage: 24.8,
          revenuePerInstance: 1950,
          monthlyRevenue: [
            { month: 'Jan 2024', revenue: 3200 },
            { month: 'Feb 2024', revenue: 3800 },
            { month: 'Mar 2024', revenue: 4100 },
            { month: 'Apr 2024', revenue: 4500 },
            { month: 'May 2024', revenue: 4200 },
            { month: 'Jun 2024', revenue: 4800 },
            { month: 'Jul 2024', revenue: 5200 },
            { month: 'Aug 2024', revenue: 4900 },
          ],
          topPerformingInstances: [
            { instance_id: '1', instance_number: 'AC-001', revenue: 2850, rental_days: 85 },
            { instance_id: '2', instance_number: 'AC-003', revenue: 2680, rental_days: 78 },
            { instance_id: '3', instance_number: 'AC-007', revenue: 2520, rental_days: 72 },
            { instance_id: '4', instance_number: 'AC-002', revenue: 2450, rental_days: 69 },
            { instance_id: '5', instance_number: 'AC-012', revenue: 2380, rental_days: 68 },
          ],
        });
        setLoading(false);
      }, 500);
    } catch (err) {
      console.error('Error loading revenue stats:', err);
      setLoading(false);
    }
  };

  const getROIBadge = (roi: number) => {
    if (roi >= 20) return { variant: 'default' as const, label: 'Excellent' };
    if (roi >= 15) return { variant: 'secondary' as const, label: 'Good' };
    if (roi >= 10) return { variant: 'warning' as const, label: 'Fair' };
    return { variant: 'destructive' as const, label: 'Poor' };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">Loading revenue data...</div>
      </div>
    );
  }

  const roiBadge = getROIBadge(stats.roiPercentage);

  return (
    <div className="space-y-6">
      {/* Key Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{stats.totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              All-time rental income
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Daily Rate</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{stats.averageDailyRate}</div>
            <p className="text-xs text-muted-foreground">
              Per instance per day
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Return on Investment</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.roiPercentage}%</div>
            <div className="mt-2">
              <Badge variant={roiBadge.variant}>{roiBadge.label}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue per Unit</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{stats.revenuePerInstance.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Average per asset
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Revenue Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Revenue Trend</CardTitle>
          <CardDescription>Revenue performance over the last 8 months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats.monthlyRevenue.map((month) => (
              <div key={month.month} className="flex items-center justify-between">
                <span className="text-sm font-medium">{month.month}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${(month.revenue / Math.max(...stats.monthlyRevenue.map(m => m.revenue))) * 100}%`
                      }}
                    />
                  </div>
                  <span className="text-sm font-bold w-20 text-right">
                   £{month.revenue.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Performing Units */}
      <Card>
        <CardHeader>
            <CardTitle>Top Performing Units</CardTitle>
          <CardDescription>Highest revenue generating instances</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats.topPerformingInstances.map((instance, index) => (
              <div key={instance.instance_id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-600">
                    #{index + 1}
                  </div>
                  <div>
                    <p className="font-medium">{instance.instance_number}</p>
                    <p className="text-sm text-gray-600">{instance.rental_days} rental days</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold">£{instance.revenue.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">
                    £{(instance.revenue / instance.rental_days).toFixed(0)}/day avg
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Revenue Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-3">
              <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium">Strong Performance</p>
                <p className="text-sm text-gray-600">
                  {stats.roiPercentage}% ROI exceeds industry average of 15%.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium">High Utilization</p>
                <p className="text-sm text-gray-600">
                  {stats.totalRentalDays} total rental days across all instances.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Financial Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Gross Revenue</span>
                <span className="text-lg font-bold">£{stats.totalRevenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Total Rental Days</span>
                <span>{stats.totalRentalDays.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Avg Revenue/Day</span>
                <span>£{(stats.totalRevenue / stats.totalRentalDays).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Projected Annual</span>
                <span className="text-lg font-bold text-green-600">
                  £{(stats.totalRevenue * 1.5).toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssetRevenueTab;
